// Vendor imports
const _ = require('lodash')
// Shared imports
const articleUtils = require('shared/model-helpers/article')
// Client lib imports
const businessEvents = require('client/lib/business-events')
const { send: ajax, FriendlyRequestError } = require('client/lib/request')
const { openWindow } = require('client/lib/window')
// Client V2 common imports
const { navigate } = require('client-v2/common/state/router/actions')
const confirmModalActions = require('client-v2/common/containers/confirm-modal/actions')
const shareModalActions = require('client-v2/common/containers/share-modal/actions')
const toastsActions = require('client-v2/common/containers/toasts/actions')
const extension = require('client-v2/common/utils/extension')
// Sibling imports
const {
  cacheReportedUrlId,
  getCachedReportedUrlIds
} = require('./utils')

/** @typedef { import('client/lib/business-events/types.d').DenormalizedPageContext } DenormalizedPageContext */
/** @typedef { import('messages/client/protobuf.d').commands.TrackGridViews.IGridView } GridView */
/** @typedef { import('messages/client/protobuf.d').commands.RateUrlType } RateUrlType */

// Action types

const ARTICLE_ADDED_TO_COLLECTION = 'ARTICLE_ADDED_TO_COLLECTION'
const ARTICLE_DOMAIN_BLOCKED = 'ARTICLE_DOMAIN_BLOCKED'
const ARTICLE_IS_MIXED_CHANGE_PENDING = 'ARTICLE_IS_MIXED_CHANGE_PENDING'
const ARTICLE_MIXED = 'ARTICLE_MIXED'
const ARTICLE_RATED = 'ARTICLE_RATED'
const ARTICLE_REMOVED_FROM_COLLECTION = 'ARTICLE_REMOVED_FROM_COLLECTION'
const ARTICLE_UNMIXED = 'ARTICLE_UNMIXED'
const ARTICLE_UPDATED = 'ARTICLE_UPDATED'
const ARTICLES_REPORTED = 'ARTICLES_REPORTED'

// Action creators

function articleAddedToCollection(article, collectionId) {
  return {
    type: ARTICLE_ADDED_TO_COLLECTION,
    payload: { article, collectionId }
  }
}

function articleRemovedFromCollection(article, collectionId) {
  return {
    type: ARTICLE_REMOVED_FROM_COLLECTION,
    payload: { article, collectionId }
  }
}

function articleIsMixedChangePending(urlId) {
  return {
    type: ARTICLE_IS_MIXED_CHANGE_PENDING,
    payload: { urlId }
  }
}

function articleMixed(article) {
  return {
    type: ARTICLE_MIXED,
    payload: { article }
  }
}

function articleUnmixed(article) {
  return {
    type: ARTICLE_UNMIXED,
    payload: { article }
  }
}

function articleUpdated(article) {
  return {
    type: ARTICLE_UPDATED,
    payload: { article, urlId: article['url_id'] }
  }
}

// Action dispatchers

function fetchArticle({ article }) {
  return async function (dispatch) {
    const result = await ajax({
      url: `/api/v0.2/url/${article.hash}`
    })
    dispatch(articleUpdated(result.url))
  }
}

function applyReportedArticlesCache() {
  return function (dispatch) {
    const urlIds = getCachedReportedUrlIds()
    if (urlIds.length) {
      dispatch({
        type: ARTICLES_REPORTED,
        payload: { urlIds }
      })
    }
  }
}

function navigateToArticleContent(options) {
  if (options.event && options.event.altKey) {
    return navigateToArticleProfile(options)
  }
  /**
   * We can't use `async`/`await` here because of user-agent pop-up blocking.
   * `window.open` **must** be called as a direct result of user action,
   * but `async`/`await` defers execution, triggering the pop-up blocker.
   */
  return function (dispatch, getState) {
    const { id: userId, referralToken } = getState().user
    const { article, pageContext } = options
    const { hash, url, 'url_id': urlId } = article
    extension.showUrl(article, { recState: pageContext, referralToken })
    .catch(() => {
      openWindow(`/referral/!${hash}?referral_token=${referralToken}`)
    })
    // Send tracking calls
    return Promise.all([
      businessEvents.trackProtobuf('TrackContentView', {
        userId,
        urlId: article['url_id'],
        viewType: 'CONTENT_VIEW'
      }, {
        pageContext
      }),
      businessEvents.trackSegment('show-web-content', {
        hostname: article.hostname,
        url,
        'url_id': urlId
      }, {
        pageContext
      })
    ])
  }
}

function navigateToArticleDomainSearch({ article, event, pageContext }) {
  return navigate({
    event,
    href: articleDomainSearchHref(article),
    pageContext
  })
}

function navigateToArticleProfile({ article, event, pageContext }) {
  return navigate({
    event,
    href: articleProfileHref(article),
    pageContext
  })
}

function blockArticleDomain({ article, pageContext }) {
  return async function (dispatch, getState) {
    const { hostname, 'url_id': urlId } = article
    const userId = getState().user.id
    try {
      await ajax({
        url: '/api/v0.1/reports/domain',
        method: 'post',
        data: {
          'url_id': urlId,
          'page_context': pageContext
        }
      })
      await businessEvents.trackSegment('report-domain', {
        'url_id': urlId,
        'reporter_id': userId
      }, {
        pageContext
      })
      extension.filterDomain(hostname)
      dispatch({
        type: ARTICLE_DOMAIN_BLOCKED,
        payload: { hostname }
      })
      dispatch(confirmModalActions.alert({
        heading: 'Domain Muted',
        subheading: `
          "${hostname}" will no longer be recommended to you,
          but you may still see content from this domain in
          search results and pages curated by users.
        `
      }))
    } catch (err) {
      dispatch(confirmModalActions.alert({
        heading: 'Sorry.',
        subheading: 'This domain could not be muted at this time. Please try again later.'
      }))
      throw err
    }
  }
}

/**
 * @param {Object} options
 * @param {ArticleModel} options.article
 * @param {'up' | 'down' | 'none'} options.rating
 * @param {DenormalizedPageContext} options.pageContext
 * @returns {function} Redux Thunk
 */
function rateArticle({ article, rating, pageContext }) {
  return async function (dispatch, getState) {
    const state = getState()
    /** @type {RateUrlType} */
    const [ ratingType, message ] = (() => {
      switch (rating) {
        case 'up':   return [ 'RATE_URL_UP', `You'll see more posts like this` ]
        case 'down': return [ 'RATE_URL_DOWN', `You'll see fewer posts like this` ]
        default:     return [ 'RATE_URL_NONE', null ]
      }
    })()
    const urlId = article['url_id']
    const userId = state.user.id
    const params = { ratingType, urlId, userId }
    await businessEvents.trackProtobuf('RateUrl', params, {
      commandUrl: '/api/v0.1/url/rate',
      pageContext
    })
    dispatch({
      type: ARTICLE_RATED,
      payload: { urlId, rating, pageContext }
    })
    if (message) {
      dispatch(toastsActions.showNotice({ message }))
    }
  }
}

function reportArticle({ article, recReason, reportReason, pageContext }) {
  return async function (dispatch, getState) {
    const urlId = article['url_id']
    const userId = getState().user.id
    if (reportReason === 'copyright') {
      if (window.Intercom.trackEvent) {
        // trackEvent may not always be enabled since
        // we don't always capture user data for Intercom
        // see views/partials/scripts/intercom.pug
        window.Intercom.trackEvent('report copyright', {
          'article url': article.url,
          'mix url': window.location.href,
          'url_id': urlId
        })
      }
      window.Intercom('showNewMessage', '')
      return
    }
    if (reportReason === 'miscat') {
      await Promise.all([
        businessEvents.trackProtobuf('ReportUrlMiscat', {
          userId,
          urlId,
          interestKey: recReason.items[0].key
        }, {
          pageContext
        }),
        businessEvents.trackSegment('report-url', {
          'url_id': urlId,
          'reporter_id': userId,
          context: reportReason
        }, {
          pageContext
        })
      ])
      dispatch(confirmModalActions.alert({
        subheading: 'Got it — thanks for letting us know!'
      }))
      return
    }
    try {
      await ajax({
        url: '/api/v0.1/reports/url',
        method: 'post',
        data: {
          'url_id': urlId,
          context: reportReason,
          'page_context': pageContext
        }
      })
      await businessEvents.trackSegment('report-url', {
        'url_id': urlId,
        'reporter_id': userId,
        context: reportReason
      }, {
        pageContext
      })
      extension.clearUrlCache(urlId)
      cacheReportedUrlId(urlId)
      dispatch({
        type: ARTICLES_REPORTED,
        payload: { urlIds: [ urlId ] }
      })
      dispatch(confirmModalActions.alert({
        subheading: 'Got it — thanks for letting us know!'
      }))
    } catch (err) {
      dispatch(confirmModalActions.alert({
        heading: 'Sorry.',
        subheading: 'This post could not be reported at this time. Please try again later.'
      }))
      throw err
    }
  }
}

function showArticleActionsModal(options) {
  return async function (dispatch) {
    const action = await dispatch(confirmModalActions.show({
      heading: 'Actions for this Post',
      buttons: articleActionsModalButtons(options),
      dismissOnBackdropClick: true
    }))
    switch (action) {
      case 'boring':
      case 'spam':
      case 'miscat':
        return dispatch(reportArticle({ ...options, reportReason: action }))
      case 'report':
        return dispatch(showArticleReportsModal(options))
      case 'block':
        return dispatch(blockArticleDomain(options))
      case 'add-to-collections':
        return dispatch(showCollectionSelectModal(options))
      case 'share':
        return dispatch(showArticleShareModal(options))
    }
  }
}

function showArticleReportsModal(options) {
  return async function (dispatch) {
    const reportReason = await dispatch(confirmModalActions.show({
      heading: 'Report Post',
      subheading: 'What is your reason for reporting this post?',
      buttons: [
        { label: 'It displays a sensitive image', value: 'image' },
        { label: `It's abusive or harmful`, value: 'nsfl' },
        { label: 'The link is broken', value: 'dead' },
        { label: 'This is my intellectual property', value: 'copyright' },
        { label: 'Cancel' }
      ],
      closeOnBackdropClick: true
    }))
    if (reportReason) {
      return dispatch(reportArticle({ ...options, reportReason }))
    }
  }
}

function showArticleProfileModal(options) {
  // Mitigate circular dependencies
  const articleProfileModalActions = require('client-v2/common/containers/article-profile/state/modal-actions')
  // Allow opening article profile in a new tab instead of popping the modal
  if (options.event && options.event.altKey) {
    return navigateToArticleProfile(options)
  }
  return function (dispatch, getState) {
    const { articleProfileModal } = getState()
    if (typeof articleProfileModal === 'undefined') {
      // Fall-back to article profile page if modal is not available
      return dispatch(navigateToArticleProfile(options))
    }
    return dispatch(articleProfileModalActions.show(options))
  }
}

function showArticleShareModal({ article }) {
  return shareModalActions.show({ model: article })
}

function showAddUrlModal(options) {
  // Work-around circular dependency
  const contentAddModalActions = require('client-v2/common/containers/content-add-modal/actions')
  return async function (dispatch) {
    return dispatch(contentAddModalActions.show(options))
  }
}

function showCollectionSelectModal(options) {
  // Work-around circular dependencies
  const collectionSelectModalActions = require('client-v2/common/containers/collection-select-modal/actions')
  return async function (dispatch) {
    return dispatch(collectionSelectModalActions.show(options))
  }
}

function handleArticleAddAction(options) {
  // Work-around circular dependencies
  const ArticleModelRecord = require('./record')
  return async function (dispatch) {
    let { article } = options
    if (article.isMixed === ARTICLE_IS_MIXED_CHANGE_PENDING) {
      return // Ignore actions while pending
    }
    if (!article.isMixed) {
      const result = await dispatch(addToMixedCollection(options))
      /**
       * Update the state of the passed-in article based on the successful
       * "Mix" action before passing it to the `collectionSelectModal`;
       * otherwise the "Ummix" button will not be available.
       */
      article = article instanceof ArticleModelRecord
      ? article.mergeDeep(result.post)
      : { ...article, ...result.post }
    }
    return dispatch(showCollectionSelectModal({ ...options, article }))
  }
}

function addToMixedCollection({ article, pageContext }) {
  const collectionSelectModalAPI = require('client-v2/common/containers/collection-select-modal/api')
  return async function (dispatch) {
    const urlId = article['url_id']
    dispatch(articleIsMixedChangePending(urlId))
    try {
      const result = await collectionSelectModalAPI.addToMixedCollection({ urlId, pageContext })
      dispatch(articleUpdated(result.post))
      dispatch(articleMixed(result.post))
      extension.clearUrlCache(urlId)
      dispatch(toastsActions.showNotice({
        message: 'Mixed to your profile'
      }))
      return result
    } catch (err) {
      // Cancel pending state; revert article
      dispatch(articleUpdated(article))
      // Present the error as an alert dialog
      let message = 'We could not mix this post. Please try again later.'
      if (err instanceof FriendlyRequestError) {
        message = err.message
      }
      dispatch(confirmModalActions.alert({
        heading: 'Sorry.',
        subheading: message
      }))
      throw err
    }
  }
}

function trackCardView({ article, pageContext }) {
  return async function (dispatch, getState) {
    const userId = getState().user.id
    await Promise.all([
      businessEvents.trackProtobuf('TrackContentView', {
        userId,
        urlId: article['url_id'],
        viewType: 'CARD_VIEW'
      }, {
        pageContext
      }),
      businessEvents.trackSegment('view-card', {
        'url_id': article['url_id']
      }, {
        pageContext
      })
    ])
  }
}

function trackContentView({ article, pageContext }) {
  return async function (dispatch, getState) {
    const userId = getState().user.id
    const urlId = article['url_id']
    await Promise.all([
      businessEvents.trackProtobuf('TrackContentView', {
        userId,
        urlId,
        viewType: 'CONTENT_VIEW'
      }, {
        pageContext
      }),
      businessEvents.trackSegment('show-web-content', {
        hostname: article.hostname,
        urlId
      }, {
        pageContext
      })
    ])
  }
}

/**
 * Debounce `TrackGridViews` and send GridView items in batches
 * @see https://app.asana.com/0/102902769366319/1159662537839112/f
 */
const TRACK_GRID_VIEWS_DEBOUNCE_DELAY = 10 // ms
/** @type {Map.<DenormalizedPageContext, GridView[]>} */
const trackGridViewsBatches = new Map()
/** @type {number|void} */
let trackGridViewsDebounceTimer

function trackGridView({ article, pageContext, recPosition }) {
  return function (dispatch) {
    const gridView = {
      urlId: article['url_id'],
      position: recPosition
    }
    enqueue({ gridView, pageContext })
    debounce(() => dispatch(flushGridViews(trackGridViewsBatches)))
  }

  function enqueue({ gridView, pageContext }) {
    for (let [ queuedPageContext, queuedGridViews ] of trackGridViewsBatches) {
      if (_.isMatch(pageContext, queuedPageContext)) {
        queuedGridViews.push(gridView)
        return
      }
    }
    trackGridViewsBatches.set(pageContext, [ gridView ])
  }

  function debounce(callback) {
    if (trackGridViewsDebounceTimer) {
      clearTimeout(trackGridViewsDebounceTimer)
    }
    trackGridViewsDebounceTimer = setTimeout(
      () => {
        callback()
        trackGridViewsDebounceTimer = null
      },
      TRACK_GRID_VIEWS_DEBOUNCE_DELAY
    )
  }
}

/**
 * @param {Map.<DenormalizedPageContext, GridView[]>} trackGridViewsByPageContext
 */
function flushGridViews(trackGridViewsByPageContext) {
  return async function (dispatch, getState) {
    const userId = getState().user.id
    const promises = []
    for (let [ pageContext, gridViews ] of trackGridViewsByPageContext) {
      promises.push(businessEvents.trackProtobuf('TrackGridViews', {
        userId,
        gridViews
      }, {
        pageContext
      }))
    }
    trackGridViewsByPageContext.clear()
    return Promise.all(promises)
  }
}

// Helpers

function articleProfileHref(article) {
  return articleUtils.prettyPath(article.hash, article.title)
}

function articleDomainSearchHref(article) {
  return `/search/${article.hostname}/domain`
}

function articleActionsModalButtons({ article, recReason }) {
  return [
    { label: `Add this post to your collections`, value: 'add-to-collections' },
    ...miscatActions(),
    ...reportActions(),
    { label: 'Cancel' }
  ]

  function miscatActions() {
    /**
     * Allow users to report miscategorized recommendations (interests-only)
     * @see https://app.asana.com/0/102902769366319/1159484167293067/f
     */
    const firstRecReasonItem = _.get(recReason, 'items[0]')
    if (_.get(firstRecReasonItem, 'type') === 'interest') {
      return [
        { label: `This is not related to "${firstRecReasonItem.name}"`, value: 'miscat' }
      ]
    }
    return []
  }

  function reportActions() {
    /**
     * Prevent users from reporting their own content
     * @see https://app.asana.com/0/102902769366319/1111869557372309/f
     */
    if (article.isMixed) {
      return []
    }
    return [
      { label: `I'm not interested in this`, value: 'boring' },
      { label: `This is spam`, value: 'spam' },
      { label: `Stop recommending ${article.hostname}`, value: 'block' },
      { label: 'Additional report options', value: 'report' }
    ]
  }
}

// Action exports

module.exports = {
  // Types
  ARTICLE_ADDED_TO_COLLECTION,
  ARTICLE_DOMAIN_BLOCKED,
  ARTICLE_IS_MIXED_CHANGE_PENDING,
  ARTICLE_MIXED,
  ARTICLE_RATED,
  ARTICLE_REMOVED_FROM_COLLECTION,
  ARTICLE_UNMIXED,
  ARTICLE_UPDATED,
  ARTICLES_REPORTED,
  // Creators
  articleAddedToCollection,
  articleIsMixedChangePending,
  articleMixed,
  articleRemovedFromCollection,
  articleUnmixed,
  articleUpdated,
  // Dispatchers
  addToMixedCollection,
  applyReportedArticlesCache,
  blockArticleDomain,
  fetchArticle,
  handleArticleAddAction,
  navigateToArticleContent,
  navigateToArticleDomainSearch,
  navigateToArticleProfile,
  rateArticle,
  reportArticle,
  showArticleActionsModal,
  showArticleProfileModal,
  showArticleReportsModal,
  showArticleShareModal,
  showAddUrlModal,
  showCollectionSelectModal,
  trackCardView,
  trackContentView,
  trackGridView,
  // Helpers
  articleProfileHref,
  articleDomainSearchHref
}
